import * as React from 'react';
import { graphql } from 'react-apollo';

import { compose } from 'recompose';

import connectToRouter from 'js/lib/connectToRouter';
import contentfulGql from 'js/lib/contentfulGql';
import user from 'js/lib/user';

import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';

import type {
  GetContentfulSeoDataQuery,
  GetContentfulSeoDataQueryVariables,
  SeoFieldsFragment,
} from '../__generated__/contentfulTypes';

type OuterProps = {
  isDegreePage?: boolean;
};

type PropsFromRouter = {
  pathname: string;
  isPreview: boolean;
} & OuterProps;

type Props = OuterProps & {
  seoContent?: SeoFieldsFragment | null;
};

const CourseraMetatagsCms = ({ seoContent, isDegreePage }: Props) => {
  if (!seoContent || !seoContent.pageLocation) {
    return null;
  }

  const {
    metaTitle,
    metaDescription,
    enableCourseraDescriptionSuffix,
    enableCourseraSuffix,
    enableCrawlerIndexing,
    enableDescriptionLimit,
    enableLanguageSubdomainLinks,
    imageUrl,
    pageUrl,
    previousPageUrl,
    nextPageUrl,
    canonicalLinkUrl,
  } = seoContent;

  const disableDescriptionSuffix = !enableCourseraDescriptionSuffix || isDegreePage;
  const disableDescriptionLimit = !enableDescriptionLimit || isDegreePage;

  return (
    <CourseraMetatags
      metaNameAndProperties={{
        title: metaTitle ?? undefined,
        description: metaDescription ?? undefined,
        absolutePageHref: pageUrl ?? undefined,
        absoluteImageHref: imageUrl ?? undefined,
        disableCourseraSuffix: !enableCourseraSuffix,
        disableCourseraDescriptionSuffix: disableDescriptionSuffix,
        disableCrawlerIndexing: !enableCrawlerIndexing,
        disableDescLimit: disableDescriptionLimit,
        ogAndTwitterTitle: isDegreePage ? (metaTitle ?? undefined) : undefined,
        productPrimaryLanguage: 'en',
      }}
      paginationProperties={{
        absoluteNextPageHref: nextPageUrl ?? undefined,
        absolutePrevPageHref: previousPageUrl ?? undefined,
        canonicalLinkHrefOverride: canonicalLinkUrl ?? undefined,
      }}
      hrefLangOverrides={(links: any) => (enableLanguageSubdomainLinks ? links : [])}
    />
  );
};

const SeoQuery = contentfulGql`#graphql
  query getContentfulSeoData($pageLocation: String!) {
    seoDataCollection(where:{
      pageLocation: $pageLocation
    }) {
      items {
        ...SeoFields
      }
    }
  }

  fragment SeoFields on SeoData {
    metaTitle,
    metaDescription,
    enableCourseraDescriptionSuffix,
    enableCourseraSuffix,
    enableCrawlerIndexing,
    enableDescriptionLimit,
    enableLanguageSubdomainLinks,
    imageUrl,
    pageUrl,
    pageLocation,
    previousPageUrl,
    nextPageUrl,
    canonicalLinkUrl,
  }
`;

export default compose<Props, OuterProps>(
  connectToRouter((router) => {
    const { query, pathname } = router.location;

    const previewParam = query.preview;
    const isPreview = user.isSuperuser() && !!previewParam && Number(previewParam) === 1;

    return {
      isPreview,
      pathname,
    };
  }),
  graphql<PropsFromRouter, GetContentfulSeoDataQuery, GetContentfulSeoDataQueryVariables, Props>(SeoQuery, {
    options: ({ pathname, isPreview }) => {
      return {
        variables: { pageLocation: pathname },
        // Only send a request through the Preview API if the user can preview
        context: { clientName: isPreview ? 'contentfulPreviewGql' : 'contentfulGql' },
        errorPolicy: 'all',
      };
    },
    props: (response) => ({
      isDegreePage: response.ownProps.isDegreePage,
      seoContent: response?.data?.seoDataCollection?.items[0],
    }),
  })
)(CourseraMetatagsCms);

export const BaseComponent = CourseraMetatagsCms;
